import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Loader2, Image as ImageIcon } from 'lucide-react';

interface SkuMetadata {
  sku: string;
  name: string | null;
  image_url: string | null;
  reference_price: number | null;
  first_product_title?: string;
  first_product_image?: string | null;
}

interface SkuEditModalProps {
  isOpen: boolean;
  sku: string;
  metadata: SkuMetadata | null;
  onClose: () => void;
  onSave: (data: {
    new_sku?: string;
    name: string | null;
    image_url: string | null;
    reference_price: number | null;
  }) => Promise<void>;
}

const SkuEditModal: React.FC<SkuEditModalProps> = ({
  isOpen,
  sku,
  metadata,
  onClose,
  onSave,
}) => {
  const { t } = useTranslation();
  const [skuId, setSkuId] = useState(sku);
  const [name, setName] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [referencePrice, setReferencePrice] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setSkuId(sku);
      setName(metadata?.name || metadata?.first_product_title || '');
      setImageUrl(metadata?.image_url || metadata?.first_product_image || '');
      setReferencePrice(
        metadata?.reference_price !== null && metadata?.reference_price !== undefined
          ? String(metadata.reference_price)
          : ''
      );
      setError(null);
    }
  }, [isOpen, sku, metadata]);

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);

    try {
      await onSave({
        new_sku: skuId !== sku ? skuId : undefined,
        name: name.trim() || null,
        image_url: imageUrl.trim() || null,
        reference_price: referencePrice ? parseFloat(referencePrice) : null,
      });
      onClose();
    } catch (err: any) {
      setError(err.message || t('skus.edit_modal.save_error'));
    } finally {
      setIsSaving(false);
    }
  };

  const formatPrice = (value: string) => {
    // Remove non-numeric characters except dots
    const cleaned = value.replace(/[^\d.]/g, '');
    // Ensure only one decimal point
    const parts = cleaned.split('.');
    if (parts.length > 2) {
      return parts[0] + '.' + parts.slice(1).join('');
    }
    return cleaned;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
          <h2 className="text-lg font-semibold text-gray-800">
            {t('skus.edit_modal.title')}
          </h2>
          <button
            onClick={onClose}
            className="p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-4 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
              {error}
            </div>
          )}

          {/* SKU ID */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('skus.edit_modal.sku_id')}
            </label>
            <input
              type="text"
              value={skuId}
              onChange={(e) => setSkuId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
              placeholder={t('skus.edit_modal.sku_id_placeholder')}
            />
            {skuId !== sku && skuId.trim() && (
              <p className="mt-1 text-xs text-amber-600">
                {t('skus.edit_modal.sku_id_warning')}
              </p>
            )}
          </div>

          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('skus.edit_modal.name')}
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
              placeholder={t('skus.edit_modal.name_placeholder')}
            />
            <p className="mt-1 text-xs text-gray-500">
              {t('skus.edit_modal.name_help')}
            </p>
          </div>

          {/* Image URL */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('skus.edit_modal.image')}
            </label>
            <div className="flex gap-3">
              <div className="flex-1">
                <input
                  type="text"
                  value={imageUrl}
                  onChange={(e) => setImageUrl(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                  placeholder={t('skus.edit_modal.image_placeholder')}
                />
              </div>
              {/* Image preview */}
              <div className="flex-shrink-0 w-16 h-16 border border-gray-200 rounded-lg overflow-hidden bg-gray-50 flex items-center justify-center">
                {imageUrl ? (
                  <img
                    src={imageUrl}
                    alt="Preview"
                    className="w-full h-full object-contain"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                      (e.target as HTMLImageElement).nextElementSibling?.classList.remove('hidden');
                    }}
                  />
                ) : (
                  <ImageIcon className="h-6 w-6 text-gray-400" />
                )}
                <ImageIcon className="h-6 w-6 text-gray-400 hidden" />
              </div>
            </div>
            <p className="mt-1 text-xs text-gray-500">
              {t('skus.edit_modal.image_help')}
            </p>
          </div>

          {/* Reference Price */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('skus.edit_modal.reference_price')}
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
              <input
                type="text"
                value={referencePrice}
                onChange={(e) => setReferencePrice(formatPrice(e.target.value))}
                className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                placeholder={t('skus.edit_modal.reference_price_placeholder')}
              />
            </div>
            <p className="mt-1 text-xs text-gray-500">
              {t('skus.edit_modal.reference_price_help')}
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            disabled={isSaving}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            {t('skus.edit_modal.cancel')}
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving || !skuId.trim()}
            className="px-4 py-2 text-sm font-medium text-white bg-pink-500 rounded-lg hover:bg-pink-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isSaving && <Loader2 className="h-4 w-4 animate-spin" />}
            {t('skus.edit_modal.save')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SkuEditModal;
